import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type {
  ErrorPutInputsMasterFixturesByStoreShelf,
  PayloadPutInputsMasterFixturesByStoreShelf,
} from "@CommonApi/inputs/master-fixtures/by-store-shelf";
import {
  RoutePostMerchflows_merchflowId_DividerRulesInterpret,
  type PayloadPostMerchflows_merchflowId_DividerRulesInterpret,
  type ResponsePostMerchflows_merchflowId_DividerRulesInterpret,
} from "@CommonApi/merchflows/_merchflowId_/divider-rules/interpret";
import type { TellOShelfProposal } from "@CommonUtils/pog/tellO";
import { putInputsMasterFixturesByStoreShelfReq } from "src/api";
import { translate } from "src/i18n/translate";
import { DEFAULT_LENGTH_UNIT, formatLengthError } from "src/utils/lengthUnit";
import { API, postRequest } from "src/utils/axios";

export const TELL_O_TIMEOUT_MS = 6000;

export type TellOInterpretResult =
  | { status: "ok"; response: ResponsePostMerchflows_merchflowId_DividerRulesInterpret }
  | { status: "offline" }
  | { status: "refused" }
  | { status: "failed" };

export type TellOInterpret = (
  payload: PayloadPostMerchflows_merchflowId_DividerRulesInterpret,
) => Promise<TellOInterpretResult>;

/** Null means saved; otherwise the result is the localized refusal reason. */
export type TellOSaveShelf = (proposal: TellOShelfProposal) => Promise<string | null>;

export const interpretDividerRules =
  (merchflowId: number, timeoutMs = TELL_O_TIMEOUT_MS): TellOInterpret =>
  async (payload) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const { data } = await postRequest<ResponsePostMerchflows_merchflowId_DividerRulesInterpret>(
        `${API}${RoutePostMerchflows_merchflowId_DividerRulesInterpret}`,
        { params: { merchflowId }, payload: { ...payload }, signal: controller.signal },
      );
      return { status: "ok", response: data };
    } catch (error) {
      if (!isAxiosError(error)) return { status: "offline" };
      const status = error.response?.status;
      if (status === undefined || status >= 500) return { status: "offline" };
      return status === 400 ? { status: "refused" } : { status: "failed" };
    } finally {
      clearTimeout(timer);
    }
  };

export const tellOShelfPayload = (
  merchflowId: number,
  proposal: TellOShelfProposal,
): PayloadPutInputsMasterFixturesByStoreShelf => {
  const where = {
    merchflowId,
    storeCode: proposal.storeCode,
    bayNo: proposal.bayNo,
    shelfNo: proposal.shelfNo,
  };
  return proposal.feature === "dividers"
    ? { ...where, dividersSetByHand: proposal.dividersSetByHand }
    : { ...where, loaderDepth: proposal.loaderDepth };
};

export const tellONotSavedText = (error: unknown): string => {
  const body = (isAxiosError(error) ? error.response?.data : undefined) as
    | Partial<ErrorPutInputsMasterFixturesByStoreShelf>
    | undefined;
  return body?.code
    ? formatLengthError({ code: body.code, params: body.params }, DEFAULT_LENGTH_UNIT)
    : translate("common", "planogramRules.tellO.notSaved");
};

export const useSaveTellOShelf = (merchflowId: number): TellOSaveShelf => {
  const { mutateAsync } = useMutation({
    mutationFn: async (proposal: TellOShelfProposal) =>
      (await putInputsMasterFixturesByStoreShelfReq(tellOShelfPayload(merchflowId, proposal))).data,
  });
  return useCallback(
    async (proposal) => {
      try {
        await mutateAsync(proposal);
        return null;
      } catch (error) {
        return tellONotSavedText(error);
      }
    },
    [mutateAsync],
  );
};
